import { useRouteError } from "react-router-dom";
import { captureException, showReportDialog } from "@sentry/react";
import { memo, useEffect } from "react";
import ErrorFallback from "../error-fallback";

type SerializableError = {
    name?: string;
    message?: string;
};

/**
 * Determines whether the given error represents a failed dynamic import / chunk load.
 *
 * Handles both explicit `ChunkLoadError` names and error messages that match
 * the "Loading chunk ... failed" pattern often thrown by bundlers.
 *
 * @param {unknown} error - The error thrown by the router or runtime.
 * @returns {error is SerializableError} `true` if the error is a chunk load error, otherwise `false`.
 */
const isChunkLoadError = (error: unknown): error is SerializableError => {
    if (!error || typeof error !== "object") {
        return false;
    }

    const { name, message } = error as SerializableError;

    if (name === "ChunkLoadError") {
        return true;
    }

    if (typeof message === "string" && /Loading chunk[\s\S]*failed/i.test(message)) {
        return true;
    }

    return false;
};

/**
 * React Router error boundary fallback that reports errors to Sentry and
 * handles transient chunk loading failures gracefully.
 *
 * - For chunk load errors, triggers a full page reload and avoids flashing
 *   the generic crash UI.
 * - For all other errors, captures the exception in Sentry and opens the
 *   Sentry report dialog, then renders a shared `ErrorFallback` UI.
 *
 * @returns {JSX.Element | null} `null` for chunk load errors (while reloading), or the `ErrorFallback` UI for other errors.
 */
export function SentryErrorFallback() {
    const error = useRouteError();

    useEffect(() => {
        if (!error) {
            return;
        }

        if (isChunkLoadError(error)) {
            window.location.reload();
            return;
        }

        const eventId = captureException(error);
        showReportDialog({ eventId: eventId ?? undefined });
    }, [error]);

    if (isChunkLoadError(error)) {
        // Avoid flashing the crash page for transient chunk errors.
        return null;
    }

    return <ErrorFallback />;
}

export default memo(SentryErrorFallback);
